import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/pricing');
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;
    const pollInterval = 2000;
    let isMounted = true;

    const pollPaymentStatus = async () => {
      if (!isMounted) return;
      
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers,
          withCredentials: true
        });

        if (!isMounted) return;
        setPaymentInfo(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        if (attempts < maxAttempts && isMounted) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else if (isMounted) {
          setStatus('timeout');
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error checking payment status:', err);
        }
        attempts++;
        if (attempts < maxAttempts && isMounted) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else if (isMounted) {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
    
    return () => {
      isMounted = false;
    };
  }, [searchParams, token, navigate]);

  return (
    <div className="min-h-screen bg-stone-white" data-testid="payment-success-page">
      <Navbar />

      <div className="pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
        <div className="max-w-md mx-auto px-4 text-center">
          {status === 'checking' && (
            <div className="animate-pulse">
              <Loader2 className="w-16 h-16 text-emerald-deep mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">
                Processing Payment...
              </h1>
              <p className="text-stone-600">Please wait while we confirm your payment</p>
            </div>
          )}

          {status === 'success' && (
            <div className="animate-fadeIn">
              <div className="w-20 h-20 bg-emerald-deep/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-emerald-deep" />
              </div>
              <h1 className="font-display text-3xl font-bold text-stone-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-stone-600 mb-2">
                Thank you for your purchase.
              </p>
              {paymentInfo && (
                <p className="text-lg font-semibold text-emerald-deep mb-6">
                  Amount: ${paymentInfo.amount?.toFixed(2)}
                </p>
              )}
              {paymentInfo?.plan_id && (
                <p className="text-stone-600 mb-6">
                  Your subscription has been upgraded to <span className="font-semibold capitalize">{paymentInfo.plan_id}</span>
                </p>
              )}
              {paymentInfo?.business_id && (
                <p className="text-stone-600 mb-6">
                  Your business is now <span className="font-semibold text-legacy-gold">Featured</span>!
                </p>
              )}
              <div className="flex flex-col gap-3">
                <Link to="/dashboard">
                  <Button className="w-full bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full">
                    Go to Dashboard
                  </Button>
                </Link>
                <Link to="/directory">
                  <Button variant="outline" className="w-full rounded-full">
                    Browse Directory
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'expired' && (
            <div>
              <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">
                Session Expired
              </h1>
              <p className="text-stone-600 mb-6">
                Your payment session has expired. Please try again.
              </p>
              <Link to="/pricing">
                <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full">
                  Try Again
                </Button>
              </Link>
            </div>
          )}

          {(status === 'timeout' || status === 'error') && (
            <div>
              <h1 className="font-display text-2xl font-bold text-stone-900 mb-2">
                Unable to Confirm Payment
              </h1>
              <p className="text-stone-600 mb-6">
                We couldn't confirm your payment status. If you were charged, please contact support.
              </p>
              <Link to="/dashboard">
                <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full">
                  Go to Dashboard
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
